import { observer } from "mobx-react-lite";
import React from "react";

import { DateTime } from "@bps/date-time";
import {
  CheckboxVisibility,
  IColumn,
  NoDataTile,
  ScrollablePane,
  ScrollbarVisibility,
  SelectionMode,
  useFormContext
} from "@bps/fluent-ui";
import {
  CorrespondenceType,
  SubmitActionCode
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { UserStorageKeys } from "@libs/gateways/user-experience/UserExperienceGateway.dtos.ts";
import { getValueWithUnits } from "@libs/utils/utils.ts";
import { useTemplatePickerFormContext } from "@modules/clinical/screens/correspondence/components/context/TemplatePickerFormContext.tsx";
import { FavouriteStar } from "@modules/clinical/screens/correspondence/components/FavouriteStar.tsx";
import {
  TemplateListItem,
  TemplatePivotItemKeys
} from "@modules/clinical/screens/correspondence/components/TemplatePivot.types.ts";
import { useTemplateManagerFormContext } from "@modules/clinical/screens/template-writer/context/TemplateManagerFormContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DetailsListField } from "@ui-components/form/DetailsListField.tsx";

import { TemplateManagementFormModel } from "../../template-writer/context/TemplateManagementFormModel.ts";
import {
  TemplateFilter,
  TemplatePickerFormModel
} from "./context/TemplatePickerFormModel.ts";

type TemplateListProps = {
  isPickerFrom?: boolean;
  templateListItems?: TemplateListItem[];
};

export const TemplateScrollablePane: React.FC<TemplateListProps> = observer(
  ({ isPickerFrom, templateListItems }) => {
    const { core, userExperience, correspondence } = useStores();

    const {
      state: { values: filter },
      actions: { reset }
    } = useFormContext<TemplateFilter>();

    const model = isPickerFrom
      ? useTemplatePickerFormContext()
      : useTemplateManagerFormContext();

    const onRenderFavourites = (item: TemplateListItem) => {
      const templateId = item.key;
      const isFavourited = model.favourites.includes(templateId);
      const onClick = async (isFilled: boolean) => {
        const updatedFavourites = isFilled
          ? model.favourites.filter(id => id !== templateId)
          : [...model.favourites, templateId];
        model.setFavourites(updatedFavourites);

        userExperience.saveToUserStorage(
          UserStorageKeys.FavouriteTemplates,
          updatedFavourites
        );
      };

      return <FavouriteStar filled={isFavourited} onClick={onClick} />;
    };

    const selectedKey = model.selectedKey;

    const sourceItems =
      model instanceof TemplateManagementFormModel && model.items != null
        ? model.items
        : templateListItems;

    const favouriteItems = sourceItems?.filter(item =>
      model.favourites.includes(item.key)
    );

    const filterItemList = () => {
      switch (selectedKey) {
        case TemplatePivotItemKeys.All:
          return sourceItems;
        case TemplatePivotItemKeys.Favourites:
          return favouriteItems;
        case TemplatePivotItemKeys.Custom:
          return sourceItems?.filter(x => x.isCustom);
        case TemplatePivotItemKeys.Autofills:
          return sourceItems?.filter(
            x => x.documentType === CorrespondenceType.Autofill
          );
        default:
          return sourceItems?.filter(x => x.documentType === selectedKey);
      }
    };

    const filterItems = filterItemList();

    const items = TemplatePickerFormModel.getItemsByFilter(
      filterItems ?? [],
      filter,
      core.accountInfo?.username
    );

    const columns: IColumn[] = [
      {
        key: "favourite",
        name: "",
        minWidth: 20,
        maxWidth: 20,
        onRender: onRenderFavourites
      },
      {
        key: "details",
        name: "Details",
        fieldName: "details",
        minWidth: 100
      },
      {
        key: "documentStatus",
        name: "",
        fieldName: "details",
        minWidth: 100,
        onRender: (item: TemplateListItem) => {
          if (item.documentStatus === SubmitActionCode.DraftCode) {
            return "Draft";
          } else {
            return item.documentStatus ===
              SubmitActionCode.PublishToEveryoneCode
              ? "Shared"
              : "Private";
          }
        }
      },
      {
        key: "documentType",
        name: "Type",
        fieldName: "type",
        minWidth: 100,
        onRender: (item: TemplateListItem) => {
          if (item.documentType === CorrespondenceType.Autofill)
            return "Autofill";

          const name =
            correspondence.ref.correspondenceTypes.keyTextValues.find(
              x => x.key === item.documentType
            );
          return name?.text ?? "";
        }
      },
      {
        key: "updated",
        name: "Updated",
        minWidth: 100,
        onRender: (item: TemplateListItem) => {
          if (!item.updated) {
            return "N/A";
          }

          const days = Math.floor(
            DateTime.now().diff(item.updated, "days").days
          );
          return `${getValueWithUnits(days, "day")} ago`;
        }
      }
    ];

    const areMultipleFiltersSelected =
      (filter.documentStatus && filter.documentStatus.length > 0) ||
      (filter.documentTypes && filter.documentTypes.length > 0) ||
      (filter.documentAuthor && filter.documentAuthor.length > 0);

    const getNoDataTitle = () => {
      if (areMultipleFiltersSelected) {
        return "No matches for your filters";
      }

      if (filter.searchText) {
        return `No matches for "${filter.searchText}"`;
      }

      return "No items can be found for this tab.";
    };

    return (
      <div style={{ height: 344, position: "relative" }}>
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
          {model.selectedKey === TemplatePivotItemKeys.Favourites &&
          !model.favourites.length ? (
            <NoDataTile
              textProps={{
                text: "No favourite templates "
              }}
              linkProps={{
                hidden: true
              }}
              showBoxShadow={false}
              greyView={true}
              styles={{
                root: {
                  height: "100%"
                }
              }}
            />
          ) : (
            <>
              {items.length > 0 ? (
                <DetailsListField
                  name="templateId"
                  required
                  items={items ?? []}
                  columns={columns}
                  selectionMode={SelectionMode.single}
                  checkboxVisibility={CheckboxVisibility.always}
                  fullRowSelection
                  setKey="filtered-detail-list"
                />
              ) : (
                <NoDataTile
                  textProps={{
                    text: getNoDataTitle()
                  }}
                  linkProps={{
                    text: "Clear filters",
                    onClick: () => reset()
                  }}
                  showBoxShadow={false}
                  greyView={true}
                  styles={{
                    root: {
                      height: "100%"
                    }
                  }}
                />
              )}
            </>
          )}
        </ScrollablePane>
      </div>
    );
  }
);
